/**
 * Saves edits that are already on screen, one save at a time per record.
 *
 * - Edits to a record that is saving wait, then go out together with the latest value per field.
 * - The queue keeps the last confirmed server value of each edited field. A failed save puts
 *   that value back, but only for fields no newer edit has touched.
 * - A server answer only updates fields no newer edit has touched.
 * - Results go to the view that made the latest edit, and only while it is open. A save that
 *   settles after that view closed reloads the record instead.
 * - A save that settles after the view switched scope (another project, document or budget)
 *   leaves the screen alone and reloads. Edits still waiting for that scope are dropped.
 *
 * - Edits belong to the session they were made in (signed-in user, sign-in and workspace). When
 *   it changes, waiting edits are dropped without being sent and saves in flight report nothing.
 *
 * Queues live outside components (see `defineRecordSaves`), so closing and reopening a view
 * keeps one queue per record.
 */

/** The screen that made an edit. */
export interface RecordSaveView {
  /** False once the view has unmounted. */
  attached: boolean;
  /** The scope the view shows now. */
  currentScope: () => string;
}

export interface RecordEdit<T extends object> {
  /** Project, document and budget the edit belongs to. */
  scope: string;
  /** The record within its scope, for example a budget line id. */
  record: string;
  changes: Partial<T>;
  /** What the screen showed before this edit, for at least the changed fields. */
  previous: Partial<T>;
  /** Sends one save. Resolves with the server's copy of the record when it returns one. */
  send: (changes: Partial<T>) => Promise<Partial<T> | void>;
  /** Writes values into the view. Only called while `view` is open on the edit's scope. */
  show: (values: Partial<T>) => void;
  /** Reloads the record's data from the server when no open view can show a result. */
  refresh: () => void;
  view: RecordSaveView;
}

export class RecordScopeLeftError extends Error {
  constructor() {
    super("The edit was not saved because a different budget is open now.");
  }
}

export class RecordSessionEndedError extends Error {
  constructor() {
    super("The edit was not saved because the user signed out or switched account or workspace.");
  }
}

interface Waiter {
  resolve: () => void;
  reject: (error: unknown) => void;
}

interface RecordState<T extends object> {
  /** Last value the server confirmed for each field with edits in progress. */
  baseline: Partial<T>;
  /** Number of the latest local edit of each field. */
  fieldEdits: Map<string, number>;
  /** Edits waiting for the save in flight, merged per field. */
  queued: Partial<T>;
  queuedWaiters: Waiter[];
  /** The latest edit, whose callbacks and view are used for results and the next save. */
  latest: RecordEdit<T>;
  saving: boolean;
  /** The session the record's edits were made in. */
  session: string;
  /** Set when the queue is disposed; a save still in flight then reports nothing. */
  ended: boolean;
}

const fieldsOf = <T extends object>(values: Partial<T>) =>
  Object.keys(values) as (keyof T & string)[];

/** The entries of `values` for the given fields only. */
export function pickFields<T extends object>(values: T, fields: readonly string[]): Partial<T> {
  return Object.fromEntries(
    Object.entries(values).filter(([field]) => fields.includes(field))
  ) as Partial<T>;
}

export class RecordSaveQueue<T extends object> {
  private readonly records = new Map<string, RecordState<T>>();
  private lastEdit = 0;

  /** `currentSession` names the signed-in user, sign-in and workspace the app has now. */
  constructor(private readonly currentSession: () => string = () => "") {}

  /** Shows the edit and saves it. Settles with the save that carries this edit. */
  save(edit: RecordEdit<T>): Promise<void> {
    const key = `${edit.scope}\u0000${edit.record}`;
    const session = this.currentSession();
    const existing = this.records.get(key);
    const state: RecordState<T> =
      existing && existing.session === session
        ? existing
        : {
            baseline: {},
            fieldEdits: new Map<string, number>(),
            queued: {},
            queuedWaiters: [],
            latest: edit,
            saving: false,
            session,
            ended: false,
          };
    if (existing && existing !== state) this.endRecord(existing);
    this.records.set(key, state);
    state.latest = edit;

    for (const field of fieldsOf(edit.changes)) {
      if (!(field in state.baseline)) state.baseline[field] = edit.previous[field];
      this.lastEdit += 1;
      state.fieldEdits.set(field, this.lastEdit);
    }
    edit.show(edit.changes);

    const settled = new Promise<void>((resolve, reject) => {
      state.queued = { ...state.queued, ...edit.changes };
      state.queuedWaiters.push({ resolve, reject });
    });
    // Callers may ignore the result; a failed save is already shown on screen.
    settled.catch(() => undefined);
    if (!state.saving) this.sendQueued(key, state);
    return settled;
  }

  /**
   * Drops every waiting edit without sending it. Saves in flight finish without calling back.
   * Call it when the user signs out or switches account or workspace.
   */
  dispose(): void {
    this.records.forEach((state) => this.endRecord(state));
    this.records.clear();
  }

  private endRecord(state: RecordState<T>) {
    state.ended = true;
    state.queued = {};
    state.queuedWaiters.forEach((waiter) => waiter.reject(new RecordSessionEndedError()));
    state.queuedWaiters = [];
  }

  private sendQueued(key: string, state: RecordState<T>) {
    const changes = state.queued;
    const waiters = state.queuedWaiters;
    const edit = state.latest;
    state.queued = {};
    state.queuedWaiters = [];
    state.saving = true;
    const lastEditAtSend = this.lastEdit;

    new Promise<Partial<T> | void>((resolve) => resolve(edit.send(changes))).then(
      (response) => this.settle(key, state, { changes, lastEditAtSend, waiters, response }),
      (error: unknown) => this.settle(key, state, { changes, lastEditAtSend, waiters, error })
    );
  }

  private settle(
    key: string,
    state: RecordState<T>,
    sent: {
      changes: Partial<T>;
      lastEditAtSend: number;
      waiters: Waiter[];
      response?: Partial<T> | void;
      error?: unknown;
    }
  ) {
    state.saving = false;
    const failed = "error" in sent;

    // The session that made these edits is over: report nothing and send nothing more.
    if (state.ended || this.currentSession() !== state.session) {
      this.endRecord(state);
      this.forget(key, state);
      sent.waiters.forEach((waiter) => waiter.reject(new RecordSessionEndedError()));
      return;
    }

    const edit = state.latest;
    const viewOpen = edit.view.attached;

    if (viewOpen && edit.view.currentScope() !== edit.scope) {
      this.forget(key, state);
      edit.refresh();
      sent.waiters.forEach((waiter) => (failed ? waiter.reject(sent.error) : waiter.resolve()));
      state.queuedWaiters.forEach((waiter) => waiter.reject(new RecordScopeLeftError()));
      return;
    }

    const untouchedSinceSend = (field: string) =>
      (state.fieldEdits.get(field) ?? 0) <= sent.lastEditAtSend;
    const show = (values: Partial<T>) => {
      if (viewOpen && fieldsOf(values).length > 0) edit.show(values);
    };

    if (failed) {
      show(pickFields(state.baseline, fieldsOf(sent.changes).filter(untouchedSinceSend)));
      sent.waiters.forEach((waiter) => waiter.reject(sent.error));
    } else {
      const confirmed: Partial<T> = { ...sent.changes, ...(sent.response ?? {}) };
      for (const field of fieldsOf(confirmed)) {
        if (field in state.baseline) state.baseline[field] = confirmed[field];
      }
      show(pickFields(confirmed, fieldsOf(confirmed).filter(untouchedSinceSend)));
      sent.waiters.forEach((waiter) => waiter.resolve());
    }

    if (fieldsOf(state.queued).length > 0) {
      this.sendQueued(key, state);
      return;
    }
    this.forget(key, state);
    // The view that made the edits closed, so whatever opens next should load the saved data.
    if (!viewOpen) edit.refresh();
  }

  /** Removes a finished record, unless a newer session already reuses its key. */
  private forget(key: string, state: RecordState<T>) {
    if (this.records.get(key) === state) this.records.delete(key);
  }
}

/** Every queue made by `defineRecordSaves`, so a session change can stop them all. */
const liveQueues = new Set<{ dispose: () => void }>();

/**
 * Declares one kind of record save. Returns the kind's queue for an owner, normally the app's
 * QueryClient, so every view that saves this kind of record shares one queue per record.
 * `currentSession` names the signed-in user, sign-in and workspace the app has now.
 */
export function defineRecordSaves<T extends object>(
  currentSession?: () => string
): (owner: object) => RecordSaveQueue<T> {
  const queues = new WeakMap<object, RecordSaveQueue<T>>();
  return (owner) => {
    let queue = queues.get(owner);
    if (!queue) {
      queue = new RecordSaveQueue<T>(currentSession);
      queues.set(owner, queue);
      liveQueues.add(queue);
    }
    return queue;
  };
}

/** Disposes every record save queue. Waiting edits are dropped without being sent. */
export function disposeAllRecordSaves(): void {
  liveQueues.forEach((queue) => queue.dispose());
}
